import React, { useState, useEffect } from 'react'
import { 
  CreditCard, 
  History, 
  Loader, 
  AlertTriangle, 
  Clock, 
  Plus,
  Minus,
  Gift,
  Star,
  RefreshCw
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import { Link } from 'react-router-dom'

interface CreditTransaction {
  transaction_uuid: string
  credits_changed: number
  credits_used: number
  credit_type: string
  transaction_type: string
  reference_type: string | null
  description: string
  balance_after: number
  transaction_date: string
  interview_title?: string | null
  interview_name?: string | null
  coupon_code?: string | null
}

interface CreditInfo {
  user_uuid: string
  first_name: string
  last_name: string
  current_credits: number
}

interface CreditBalanceWidgetProps {
  compact?: boolean
  showHistory?: boolean
  className?: string
  linkTo?: string
}

export default function CreditBalanceWidget({ 
  compact = false, 
  showHistory = true, 
  className = '',
  linkTo
}: CreditBalanceWidgetProps) {
  const [creditInfo, setCreditInfo] = useState<CreditInfo | null>(null)
  const [transactions, setTransactions] = useState<CreditTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const loadCreditData = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true)
      if (!showLoading) setRefreshing(true)
      setError(null)

      // Call the get_user_credit_info procedure
      const response = await apiClient.request('/coupons/credits', { method: 'GET' })
      
      if (response.success && response.data) {
        const data = response.data
        
        if (data.credit_balance !== undefined) {
          setCreditInfo({
            user_uuid: data.user_uuid || '',
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            current_credits: data.credit_balance || 0
          })
        }
        
        if (data.transaction_history && Array.isArray(data.transaction_history)) {
          setTransactions(data.transaction_history.slice(0, 10)) // Show last 10 transactions
        }
      } else {
        // Fallback to existing credits endpoint
        const fallbackResponse = await apiClient.request('/users/credits', { method: 'GET' })
        if (fallbackResponse.success && fallbackResponse.data) {
          setCreditInfo({
            user_uuid: '',
            first_name: '',
            last_name: '',
            current_credits: fallbackResponse.data.total || 0
          })
          setTransactions([])
        } else {
          throw new Error(response.error || 'Failed to load credit information')
        }
      }
    } catch (err) {
      console.error('Failed to load credit data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load credit data')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    loadCreditData()
  }, [])

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'Invalid date'
    }
  }

  const getTransactionIcon = (transaction: CreditTransaction) => {
    switch (transaction.transaction_type) {
      case 'coupon_redemption':
        return <Gift className="w-4 h-4 text-green-600" />
      case 'usage':
        return <Minus className="w-4 h-4 text-red-600" />
      case 'purchase':
        return <Plus className="w-4 h-4 text-blue-600" />
      case 'bonus':
        return <Star className="w-4 h-4 text-yellow-600" />
      default:
        return <Clock className="w-4 h-4 text-gray-600" />
    }
  }

  const getTransactionColor = (transaction: CreditTransaction) => {
    return transaction.credits_changed > 0 ? 'text-green-600' : 'text-red-600'
  }

  const getTransactionText = (transaction: CreditTransaction) => {
    if (transaction.transaction_type === 'coupon_redemption') {
      return `Redeemed coupon ${transaction.coupon_code || 'unknown'}`
    }
    if (transaction.transaction_type === 'usage' && transaction.interview_title) {
      return `Interview: ${transaction.interview_title}`
    }
    return transaction.description || 'Credit transaction'
  }

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center h-24">
          <Loader className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center space-x-2 text-red-600">
          <AlertTriangle className="w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
        <button 
          onClick={() => loadCreditData()}
          className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
        >
          Try again
        </button>
      </div>
    )
  }

  const renderCompactSummaryContent = () => (
    <>
      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
        <CreditCard className="w-5 h-5 text-white" />
      </div>
      <div>
        <div className="text-lg font-bold text-blue-900">{creditInfo?.current_credits || 0}</div>
        <div className="text-sm text-blue-700">Credits</div>
      </div>
    </>
  )

  const renderCompactSummary = () => {
    const baseClasses = `flex items-center space-x-3 ${linkTo ? 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 rounded-lg transition' : ''}`
    if (linkTo) {
      return (
        <Link to={linkTo} className={baseClasses} aria-label="View credit usage details">
          {renderCompactSummaryContent()}
        </Link>
      )
    }
    return (
      <div className={baseClasses}>
        {renderCompactSummaryContent()}
      </div>
    )
  }

  const renderFullSummary = () => {
    const content = (
      <div className="flex items-center space-x-3">
        <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
          <CreditCard className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="text-2xl font-bold text-blue-900">{creditInfo?.current_credits || 0}</div>
          <div className="text-sm text-blue-700">Available Credits</div>
        </div>
      </div>
    )

    const wrapperClasses = `flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 ${linkTo ? 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 transition' : ''}`

    if (linkTo) {
      return (
        <Link to={linkTo} className={wrapperClasses} aria-label="View detailed credit usage">
          {content}
          <div className="text-sm text-blue-700 hidden sm:block">View history →</div>
        </Link>
      )
    }

    return (
      <div className={wrapperClasses}>
        {content}
      </div>
    )
  }

  if (compact) {
    return (
      <div className={`bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-4 ${className}`}>
        <div className="flex items-center justify-between">
          {renderCompactSummary()}
          <button
            onClick={() => loadCreditData(false)}
            disabled={refreshing}
            className="text-blue-600 hover:text-blue-700 disabled:opacity-50"
            title="Refresh credit balance"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Credit Balance Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Credit Balance</h3>
          <button
            onClick={() => loadCreditData(false)}
            disabled={refreshing}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        
        {renderFullSummary()}

        {/* Usage Information */}
        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <div className="font-medium">How Credits Work</div>
              <div className="mt-1">Each interview creation uses 1 credit. Redeem coupons to add more credits.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction History */}
      {showHistory && (
        <div className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <History className="w-5 h-5 text-gray-600" />
            <h4 className="text-md font-medium text-gray-900">Recent Activity</h4>
          </div>
          
          {transactions.length > 0 ? (
            <div className="space-y-3">
              {transactions.map((transaction) => (
                <div key={transaction.transaction_uuid} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {getTransactionIcon(transaction)}
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {getTransactionText(transaction)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDate(transaction.transaction_date)}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-semibold ${getTransactionColor(transaction)}`}>
                      {transaction.credits_changed > 0 ? '+' : ''}{transaction.credits_changed}
                    </div>
                    <div className="text-xs text-gray-500">
                      Balance: {transaction.balance_after}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <History className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">No transaction history available</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
